import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDatabase } from '../contexts/DatabaseContext';
const Register = () => {
  const navigate = useNavigate();
  const {
    register,
    isLoggedIn
  } = useAuth();
  const {
    isLoaded
  } = useDatabase();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    contactNumber: '',
    deliveryAddress: '',
    agreeToTerms: false,
    userType: 'customer' // Always customer for registration
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // If already logged in, redirect to dashboard
  useEffect(() => {
    if (isLoggedIn) {
      navigate('/customer');
    }
  }, [isLoggedIn, navigate]);
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const validateForm = () => {
    const newErrors = {};
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    // Password confirmation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    // Name validation
    if (!formData.firstName) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName) {
      newErrors.lastName = 'Last name is required';
    }
    // Contact number validation
    if (!formData.contactNumber) {
      newErrors.contactNumber = 'Contact number is required';
    } else if (!/^\+?\d{10,15}$/.test(formData.contactNumber.replace(/\s/g, ''))) {
      newErrors.contactNumber = 'Please enter a valid phone number';
    }
    // Address validation - always required for customers
    if (!formData.deliveryAddress) {
      newErrors.deliveryAddress = 'Delivery address is required';
    }
    // Terms agreement
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    if (validateForm()) {
      try {
        // Create user object for the database
        const newUser = {
          Name: `${formData.firstName} ${formData.lastName}`,
          Email: formData.email,
          Password: formData.password,
          Contact: formData.contactNumber,
          Address: formData.deliveryAddress,
          User_type: 'customer',
          Date_Registered: new Date().toISOString()
        };
        // Register user using AuthContext
        const success = await register(newUser);
        if (success) {
          // Show success message
          alert('Registration successful! You are now logged in.');
          // Always redirect to customer dashboard
          navigate('/customer');
        } else {
          setErrors({
            ...errors,
            email: 'This email is already registered or an error occurred'
          });
        }
      } catch (error) {
        console.error('Registration error:', error);
        setErrors({
          ...errors,
          general: 'An error occurred during registration'
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(false);
    }
  };
  // If database is not loaded yet, show loading
  if (!isLoaded) {
    return <div className="flex justify-center items-center min-h-screen bg-amber-50" data-id="element-1867">
        <div className="text-center" data-id="element-1868">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-800 mx-auto" data-id="element-1869"></div>
          <p className="mt-4 text-amber-800" data-id="element-1870">Loading...</p>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-amber-50 py-12 px-4 sm:px-6 lg:px-8" data-id="element-1871">
      <div className="max-w-md mx-auto" data-id="element-1872">
        <div data-id="element-1873">
          <h1 className="text-center text-3xl font-extrabold text-amber-900" data-id="element-1874">
            Create your account
          </h1>
          <p className="mt-2 text-center text-sm text-amber-800" data-id="element-1875">
            Or{' '}
            <Link to="/login" className="font-medium text-amber-800 hover:text-amber-700" data-id="element-1876">
              sign in to your existing account
            </Link>
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit} data-id="element-1877">
          {errors.general && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert" data-id="element-1878">
              <span className="block sm:inline" data-id="element-1879">{errors.general}</span>
            </div>}
          <div className="rounded-md shadow-sm space-y-4" data-id="element-1880">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-1881">
              <div data-id="element-1882">
                <label htmlFor="firstName" className="block text-sm font-medium text-amber-800" data-id="element-1883">
                  First Name*
                </label>
                <input id="firstName" name="firstName" type="text" required className={`mt-1 appearance-none block w-full px-3 py-2 border ${errors.firstName ? 'border-red-300' : 'border-amber-300'} rounded-md shadow-sm placeholder-amber-400 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm`} value={formData.firstName} onChange={handleChange} data-id="element-1884" />
                {errors.firstName && <p className="text-red-500 text-xs mt-1" data-id="element-1885">
                    {errors.firstName}
                  </p>}
              </div>
              <div data-id="element-1886">
                <label htmlFor="lastName" className="block text-sm font-medium text-amber-800" data-id="element-1887">
                  Last Name*
                </label>
                <input id="lastName" name="lastName" type="text" required className={`mt-1 appearance-none block w-full px-3 py-2 border ${errors.lastName ? 'border-red-300' : 'border-amber-300'} rounded-md shadow-sm placeholder-amber-400 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm`} value={formData.lastName} onChange={handleChange} data-id="element-1888" />
                {errors.lastName && <p className="text-red-500 text-xs mt-1" data-id="element-1889">{errors.lastName}</p>}
              </div>
            </div>
            <div data-id="element-1890">
              <label htmlFor="email" className="block text-sm font-medium text-amber-800" data-id="element-1891">
                Email address*
              </label>
              <input id="email" name="email" type="email" autoComplete="email" required className={`mt-1 appearance-none block w-full px-3 py-2 border ${errors.email ? 'border-red-300' : 'border-amber-300'} rounded-md shadow-sm placeholder-amber-400 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm`} value={formData.email} onChange={handleChange} data-id="element-1892" />
              {errors.email && <p className="text-red-500 text-xs mt-1" data-id="element-1893">{errors.email}</p>}
            </div>
            <div data-id="element-1894">
              <label htmlFor="contactNumber" className="block text-sm font-medium text-amber-800" data-id="element-1895">
                Mobile Number*
              </label>
              <input id="contactNumber" name="contactNumber" type="tel" required className={`mt-1 appearance-none block w-full px-3 py-2 border ${errors.contactNumber ? 'border-red-300' : 'border-amber-300'} rounded-md shadow-sm placeholder-amber-400 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm`} placeholder="+63 912 345 6789" value={formData.contactNumber} onChange={handleChange} data-id="element-1896" />
              {errors.contactNumber && <p className="text-red-500 text-xs mt-1" data-id="element-1897">
                  {errors.contactNumber}
                </p>}
            </div>
            <div data-id="element-1898">
              <label htmlFor="deliveryAddress" className="block text-sm font-medium text-amber-800" data-id="element-1899">
                Delivery Address*
              </label>
              <textarea id="deliveryAddress" name="deliveryAddress" rows={3} required className={`mt-1 appearance-none block w-full px-3 py-2 border ${errors.deliveryAddress ? 'border-red-300' : 'border-amber-300'} rounded-md shadow-sm placeholder-amber-400 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm`} value={formData.deliveryAddress} onChange={handleChange} placeholder="Complete street address, barangay, city" data-id="element-1900" />
              {errors.deliveryAddress && <p className="text-red-500 text-xs mt-1" data-id="element-1901">
                  {errors.deliveryAddress}
                </p>}
            </div>
            <div data-id="element-1902">
              <label htmlFor="password" className="block text-sm font-medium text-amber-800" data-id="element-1903">
                Password*
              </label>
              <input id="password" name="password" type="password" required className={`mt-1 appearance-none block w-full px-3 py-2 border ${errors.password ? 'border-red-300' : 'border-amber-300'} rounded-md shadow-sm placeholder-amber-400 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm`} value={formData.password} onChange={handleChange} data-id="element-1904" />
              {errors.password && <p className="text-red-500 text-xs mt-1" data-id="element-1905">{errors.password}</p>}
            </div>
            <div data-id="element-1906">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-amber-800" data-id="element-1907">
                Confirm Password*
              </label>
              <input id="confirmPassword" name="confirmPassword" type="password" required className={`mt-1 appearance-none block w-full px-3 py-2 border ${errors.confirmPassword ? 'border-red-300' : 'border-amber-300'} rounded-md shadow-sm placeholder-amber-400 focus:outline-none focus:ring-amber-500 focus:border-amber-500 sm:text-sm`} value={formData.confirmPassword} onChange={handleChange} data-id="element-1908" />
              {errors.confirmPassword && <p className="text-red-500 text-xs mt-1" data-id="element-1909">
                  {errors.confirmPassword}
                </p>}
            </div>
            <div className="flex items-center" data-id="element-1910">
              <input id="agreeToTerms" name="agreeToTerms" type="checkbox" className={`h-4 w-4 text-amber-600 focus:ring-amber-500 border-amber-300 rounded ${errors.agreeToTerms ? 'border-red-300' : ''}`} checked={formData.agreeToTerms} onChange={handleChange} data-id="element-1911" />
              <label htmlFor="agreeToTerms" className="ml-2 block text-sm text-amber-900" data-id="element-1912">
                I agree to the{' '}
                <Link to="/terms" className="text-amber-800 hover:text-amber-700" data-id="element-1913">
                  Terms and Conditions
                </Link>
              </label>
            </div>
            {errors.agreeToTerms && <p className="text-red-500 text-xs mt-1" data-id="element-1914">{errors.agreeToTerms}</p>}
          </div>
          <div data-id="element-1915">
            <button type="submit" disabled={isSubmitting} className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${isSubmitting ? 'bg-amber-600 cursor-not-allowed' : 'bg-amber-800 hover:bg-amber-900'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500`} data-id="element-1916">
              {isSubmitting ? <span className="flex items-center" data-id="element-1917">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1918">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1919"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1920"></path>
                  </svg>
                  Creating Account...
                </span> : 'Create Account'}
            </button>
          </div>
          <div className="text-sm text-center text-amber-800" data-id="element-1921">
            By creating an account, you'll receive a 10% discount on your first
            order!
          </div>
        </form>
      </div>
    </div>;
};
export default Register;